import { create } from "zustand";
import {
  fetchServers,
  fetchServerStatus,
  type Server,
  type ServerStatusResponse,
} from "../utils/api";
import { handleApiError } from "../utils/errorHandler";

interface ServersState {
  servers: Server[];
  loading: boolean;
  error: string | null;
  selectedServer: Server | null;
  serverStatus: "server-ok" | "server-error" | null;
  selectLoading: boolean;
  selectError: string | null;
  loadServers: () => Promise<void>;
  checkServerStatus: (id: string, dryRun: boolean) => Promise<void>;
}

export const useServersStore = create<ServersState>((set, get) => ({
  servers: [],
  loading: false,
  error: null,
  selectedServer: null,
  serverStatus: null,
  selectLoading: false,
  selectError: null,

  loadServers: async () => {
    set({ loading: true, error: null });

    try {
      const servers = await fetchServers();
      set({ servers, loading: false });

      if (servers.length === 0) {
        set({ error: "Ничего не найдено." });
      }
    } catch (err: unknown) {
      const customMessages = { 404: "Сервера не найдены." };
      const errorMessage = handleApiError(
        err,
        "Ошибка загрузки серверов. Попробуйте позже.",
        customMessages
      );
      set({ error: errorMessage, loading: false });
    }
  },

  checkServerStatus: async (id: string, dryRun: boolean) => {
    const server = get().servers.find((s) => s.id === id);

    if (!server) {
      set({ selectError: "Сервер не найден." });
      return;
    }

    set({
      selectedServer: server,
      selectLoading: true,
      selectError: null,
      serverStatus: null,
    });

    try {
      const response: ServerStatusResponse = await fetchServerStatus(server.alfan, dryRun);
      const isServerDown = response.isDown === true || response.status === 'down' || !response.success;
      const status: "server-ok" | "server-error" = isServerDown ? "server-error" : "server-ok";

      set({
        selectLoading: false,
        serverStatus: status,
      });
    } catch (err: unknown) {
      const errorMessage = handleApiError(
        err,
        "Ошибка проверки статуса сервера. Попробуйте позже."
      );

      set({
        selectLoading: false,
        selectError: errorMessage,
      });
    }
  },
}));